import { Editor } from '@tiptap/core';
import { PopoverProps } from 'antd';
import { ComponentType, FC, useCallback, useMemo } from 'react';
import {
  EditorPopoverContentProps,
  EditorPopoverState,
  isEditorPopoverStateVisible,
} from './types';
import { OutsideClickHandler } from '../../outside-click-handler';
import { Popover } from '../../popover';

export interface EditorPopoverProps {
  popoverState: EditorPopoverState;
  editor: Editor;
  content: ComponentType<EditorPopoverContentProps>;
  hidePopover: VoidFunction;
  popoverContentContainerClassName?: string;
  canIgnoreOutsideClickFor?: (target: HTMLElement) => boolean;
  placement?: PopoverProps['placement'];
}
const POPOVER_CONTENT_CLASS_NAME = 'editor-popover-content';

export const EditorPopover: FC<EditorPopoverProps> = ({
  editor,
  content: Content,
  popoverState,
  hidePopover,
  popoverContentContainerClassName,
  canIgnoreOutsideClickFor,
  placement = 'topLeft',
}) => {
  const contentClass = useMemo(() => {
    return [POPOVER_CONTENT_CLASS_NAME, popoverContentContainerClassName]
      .filter(Boolean)
      .join(' ');
  }, [popoverContentContainerClassName]);
  const isOutsideClickAllowed = useCallback(
    (target: HTMLElement) => {
      return (
        editor.view.dom.contains(target) ||
        !!target.closest(`.${POPOVER_CONTENT_CLASS_NAME}`) ||
        contentClass.split(' ').some((name) => !!target.closest(`.${name}`))
      );
    },
    [contentClass, editor.view.dom],
  );

  return (
    <OutsideClickHandler
      allowed={canIgnoreOutsideClickFor ?? isOutsideClickAllowed}
      onOutsideClick={hidePopover}
    >
      <Popover
        content={
          <div className={contentClass}>
            {popoverState.visible ? (
              <Content editor={editor} closePopover={hidePopover} />
            ) : null}
          </div>
        }
        open={popoverState.visible}
        placement={placement}
      >
        <div
          style={{
            position: 'fixed',
            top: isEditorPopoverStateVisible(popoverState)
              ? popoverState.position.top
              : 0,
            left: isEditorPopoverStateVisible(popoverState)
              ? popoverState.position.left
              : 0,
            height: '20px',
          }}
        ></div>
      </Popover>
    </OutsideClickHandler>
  );
};
